// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useCallback, useEffect, useRef } from 'react';
import type {
  WizardStorageData,
  PendingUpload,
  WizardStepId,
  TransactionHistoryGroup,
  AutoFilledMap,
} from '../types/wizard';
import { WIZARD_STORAGE_VERSION, getWizardStorageKey } from '../types/wizard';

interface UseWizardStorageResult {
  storageData: WizardStorageData | null;
  storageAvailable: boolean;
  versionMismatch: boolean;
  isSaving: boolean;
  saveStepData: (stepId: WizardStepId, data: Record<string, unknown>, completed: boolean) => void;
  loadStorageData: () => WizardStorageData | null;
  updateCurrentStep: (stepId: WizardStepId) => void;
  addPendingUpload: (upload: PendingUpload) => void;
  removePendingUpload: (stepId: WizardStepId, fieldKey: string) => void;
  getPendingUploads: () => PendingUpload[];
  setTransactionHistory: (history: TransactionHistoryGroup | undefined) => void;
  setAutoFilled: (autoFilled: AutoFilledMap) => void;
  clearStorage: () => void;
}

function testLocalStorage(): boolean {
  try {
    const key = '__loa_test__';
    localStorage.setItem(key, 'test');
    const val = localStorage.getItem(key);
    localStorage.removeItem(key);
    return val === 'test';
  } catch {
    return false;
  }
}

export function useWizardStorage(token: string): UseWizardStorageResult {
  const storageKey = getWizardStorageKey(token);

  // Load localStorage synchronously so data is available on first render
  const [initialState] = useState(() => {
    const available = testLocalStorage();
    if (!available) {
      return { available, data: null as WizardStorageData | null, mismatch: false };
    }
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) {
        return { available, data: null as WizardStorageData | null, mismatch: false };
      }
      const parsed = JSON.parse(raw) as WizardStorageData;
      if (parsed.version !== WIZARD_STORAGE_VERSION) {
        localStorage.removeItem(storageKey);
        return { available, data: null as WizardStorageData | null, mismatch: true };
      }
      return { available, data: parsed, mismatch: false };
    } catch {
      localStorage.removeItem(storageKey);
      return { available, data: null as WizardStorageData | null, mismatch: false };
    }
  });

  const [storageAvailable, setStorageAvailable] = useState(initialState.available);
  const [storageData, setStorageData] = useState<WizardStorageData | null>(initialState.data);
  const [versionMismatch] = useState(initialState.mismatch);
  const [isSaving, setIsSaving] = useState(false);
  const savingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (savingTimerRef.current) {
        clearTimeout(savingTimerRef.current);
      }
    };
  }, []);

  const writeToStorage = useCallback(
    (data: WizardStorageData) => {
      if (!storageAvailable) return;
      try {
        localStorage.setItem(storageKey, JSON.stringify(data));
        setStorageData(data);
      } catch {
        // QuotaExceededError or other storage error
        setStorageAvailable(false);
      }
    },
    [storageAvailable, storageKey],
  );

  const getCurrentStorageData = useCallback((): WizardStorageData => {
    if (storageAvailable) {
      try {
        const raw = localStorage.getItem(storageKey);
        if (raw) {
          return JSON.parse(raw) as WizardStorageData;
        }
      } catch {
        // Fall through to create fresh
      }
    }

    // Return fresh structure
    return {
      version: WIZARD_STORAGE_VERSION,
      lastUpdatedAt: new Date().toISOString(),
      lastUpdatedStep: 'loa-validation',
      currentStep: 'loa-validation',
      steps: {},
      pendingUploads: [],
    };
  }, [storageAvailable, storageKey]);

  const saveStepData = useCallback(
    (stepId: WizardStepId, data: Record<string, unknown>, completed: boolean) => {
      setIsSaving(true);

      const current = getCurrentStorageData();
      current.steps = {
        ...current.steps,
        [stepId]: {
          completed,
          savedAt: new Date().toISOString(),
          data,
        },
      };
      current.lastUpdatedAt = new Date().toISOString();
      current.lastUpdatedStep = stepId;

      writeToStorage(current);

      // Show saving state briefly for visual feedback
      if (savingTimerRef.current) {
        clearTimeout(savingTimerRef.current);
      }
      savingTimerRef.current = setTimeout(() => {
        setIsSaving(false);
      }, 300);
    },
    [getCurrentStorageData, writeToStorage],
  );

  const loadStorageData = useCallback((): WizardStorageData | null => {
    if (!storageAvailable) return null;
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return null;
      return JSON.parse(raw) as WizardStorageData;
    } catch {
      return null;
    }
  }, [storageAvailable, storageKey]);

  const updateCurrentStep = useCallback(
    (stepId: WizardStepId) => {
      const current = getCurrentStorageData();
      current.currentStep = stepId;
      current.lastUpdatedAt = new Date().toISOString();
      writeToStorage(current);
    },
    [getCurrentStorageData, writeToStorage],
  );

  const addPendingUpload = useCallback(
    (upload: PendingUpload) => {
      const current = getCurrentStorageData();
      // Replace existing upload for same step+field
      current.pendingUploads = current.pendingUploads.filter(
        (u) => !(u.stepId === upload.stepId && u.fieldKey === upload.fieldKey),
      );
      current.pendingUploads.push(upload);
      writeToStorage(current);
    },
    [getCurrentStorageData, writeToStorage],
  );

  const removePendingUpload = useCallback(
    (stepId: WizardStepId, fieldKey: string) => {
      const current = getCurrentStorageData();
      current.pendingUploads = current.pendingUploads.filter(
        (u) => !(u.stepId === stepId && u.fieldKey === fieldKey),
      );
      writeToStorage(current);
    },
    [getCurrentStorageData, writeToStorage],
  );

  const getPendingUploads = useCallback((): PendingUpload[] => {
    const current = getCurrentStorageData();
    return current.pendingUploads;
  }, [getCurrentStorageData]);

  const setTransactionHistory = useCallback(
    (history: TransactionHistoryGroup | undefined) => {
      const current = getCurrentStorageData();
      if (history) {
        current.transactionHistory = history;
      } else {
        delete current.transactionHistory;
      }
      current.lastUpdatedAt = new Date().toISOString();
      writeToStorage(current);
    },
    [getCurrentStorageData, writeToStorage],
  );

  const setAutoFilled = useCallback(
    (autoFilled: AutoFilledMap) => {
      const current = getCurrentStorageData();
      current.autoFilled = autoFilled;
      current.lastUpdatedAt = new Date().toISOString();
      writeToStorage(current);
    },
    [getCurrentStorageData, writeToStorage],
  );

  const clearStorage = useCallback(() => {
    if (!storageAvailable) return;
    try {
      localStorage.removeItem(storageKey);
      setStorageData(null);
    } catch {
      // Silently fail
    }
  }, [storageAvailable, storageKey]);

  return {
    storageData,
    storageAvailable,
    versionMismatch,
    isSaving,
    saveStepData,
    loadStorageData,
    updateCurrentStep,
    addPendingUpload,
    removePendingUpload,
    getPendingUploads,
    setTransactionHistory,
    setAutoFilled,
    clearStorage,
  };
}
